import {useTranslation, localizeNewsTitle} from "../../i18n/translation";
import {localizeNews, getNewsTranslation, newsPath} from "../../i18n/news";
import Navigation from "../utils/Navigation";
import React, {useEffect, useRef, useMemo, useState} from "react";
import {NewsPhoto, NewsPreviewCard} from "./NewsCard";
import {useLocation, Link} from "react-router-dom";
import NewsContentJson from "./newsContentData.json";
import SeoHelmet from "../utils/seo";
import {DocumentationTable} from "../utils/documentationTable";
import {DoubleList, SingleList, NoStrongList,
    TableConstructor, GalleryDescription, GalleryDescriptionLinks, SilosModelsConstructor,
    ProductListParams} from "../utils/utils";
import {fetchRequestNewsView} from "../../api/news/newsApi"
import {getUUID} from "../utils/uuid";
import {useNews} from "./NewsContext";
import {useTheme} from "../header/Header"
import {EyeOutlined} from "@ant-design/icons";

import "../../css/lightTheme/publicLight.css";
import "../../css/news-refresh.css";

// Динамическая загрузка изображений

const getImage = (path) => {
    if (!path) return "";
    const clean = String(path).replace(/^\.?\//, "");
    return `/images/news/${clean}`;
}


export const processNewsData = (data, viewsMap = new Map()) => {
    return data
        .sort((a, b) => {
            const parseDate = (dateStr) => {
                const [day, month, year] = dateStr.split('.').map(Number);
                return new Date(year, month - 1, day);
            };
            return parseDate(b.date) - parseDate(a.date);
        })
        .map((news, i) => ({
            ...news,
            url: `/news/${data.length - i}`,
            image: getImage(news.preview),
            views: viewsMap.get(news.title) || 0
        }));
};

// Маппинг категорий на человекочитаемые названия
const categoryLabels = {
    production: 'Производство',
    technology: 'Технологии',
    visits: 'Визиты',
    patents: 'Патенты',
    general: 'Общее',
    exhibitions: 'Выставки',
    social: 'События',
    development: 'Развитие',
    partnership: 'Партнёрство',
    space: 'Космос'
};

const getYearFromDate = (dateStr) => {
    return Number(dateStr.split('.')[2]);
};

export function NewsPreview() {
    const {english, t} = useTranslation();
    const { newsData, refreshNews } = useNews();
    const location = useLocation();
    const { theme } = useTheme();

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);
    const [view, setView] = useState(() => {
        try {
            return localStorage.getItem('news-view') === 'list' ? 'list' : 'cards';
        } catch {
            return 'cards';
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem('news-view', view);
        } catch {
            // Переключение доступно и при запрещённом хранилище браузера.
        }
    }, [view]);
    const [page, setPage] = useState(1);
    const resultsRef = useRef(null);
    const pageSize = 12;

    // Получаем список доступных годов из новостей (сортируем по убыванию)
    const availableYears = useMemo(() => {
        if (!newsData.length) return [];
        const years = newsData.map(news => getYearFromDate(news.date)).filter(Boolean);
        const uniqueYears = [...new Set(years)];
        return uniqueYears.sort((a, b) => b - a);
    }, [newsData]);

    // Сохраняем порядок категорий из categoryLabels.
    const categories = useMemo(() => {
        if (!newsData.length) return [];

        // Получаем все категории, которые есть в новостях
        const existingCategories = new Set(newsData.map(n => n.category).filter(Boolean));

        // Создаём массив категорий в порядке, заданном в categoryLabels
        const orderedCategories = Object.keys(categoryLabels)
            .filter(cat => existingCategories.has(cat));

        return orderedCategories.map(category => ({
            category,
            label: categoryLabels[category],
            count: newsData.filter(news => news.category === category &&
                (!selectedYear || getYearFromDate(news.date) === selectedYear)).length
        }));
    }, [newsData, selectedYear]);

    // Фильтруем новости по категории и году
    const filteredNews = useMemo(() => {
        let result = newsData;
        if (selectedCategory) {
            result = result.filter(n => n.category === selectedCategory);
        }
        if (selectedYear) {
            result = result.filter(n => getYearFromDate(n.date) === selectedYear);
        }
        return result;
    }, [newsData, selectedCategory, selectedYear]);

    const totalPages = Math.ceil(filteredNews.length / pageSize);
    const currentPage = Math.min(page, Math.max(1, totalPages));
    const pageStart = (currentPage - 1) * pageSize;
    const visibleNews = filteredNews.slice(pageStart, pageStart + pageSize);
    const pageNumbers = Array.from({length: totalPages}, (_, index) => index + 1)
        .filter(number => number === 1 || number === totalPages || Math.abs(number - currentPage) <= 1);
    const changePage = number => {
        setPage(number);
        resultsRef.current?.focus({preventScroll: true});
        resultsRef.current?.scrollIntoView({block: 'start'});
    };

    const title = english
        ? `News${selectedCategory ? ` · ${t(categoryLabels[selectedCategory])}` : ''}${selectedYear ? ` · ${selectedYear}` : ''}`
        : (selectedCategory || selectedYear)
        ? `Новости${selectedCategory ? ` категории «${categoryLabels[selectedCategory] || selectedCategory}»` : ''}${selectedYear ? ` за ${selectedYear} год` : ''}`
        : 'Новости';
    const description = english
        ? 'News from Sespel: manufacturing achievements, technologies, new developments, projects and company events.'
        : 'Новости Чебоксарского предприятия «Сеспель»: производственные достижения, технологии, новые разработки, проекты и события компании.';

    useEffect(() => {
        refreshNews();
    }, [location.pathname, refreshNews]);

    const handleCategoryClick = (category) => {
        setPage(1);
        setSelectedCategory(prev => (prev === category ? null : category));
    };

    const handleYearClick = (year) => {
        setPage(1);
        setSelectedYear(prev => (prev === year ? null : year));
    };

    const clearFilters = () => {
        setPage(1);
        setSelectedCategory(null);
        setSelectedYear(null);
    };

    const hasActiveFilters = selectedCategory || selectedYear;

    return (
        <>
            <SeoHelmet
                title={hasActiveFilters ? `${title} — ${english ? 'Sespel' : 'Сеспель'}` : english ? 'Sespel News — company events and achievements' : 'Новости Сеспель — события и достижения предприятия'}
                description={description} canonicalUrl={newsPath('/news', english)} />
            <Navigation />

            <div className={`news-preview-wrapper news-hub news-redesign ${theme}`}>
                <div className={`complexBack ${theme}`}> </div>
                <header className="news-hub-heading">
                    <span className="news-kicker">{t("Сеспель / Пресс-центр")}</span>
                    <h1>{t("Новости предприятия")}</h1>
                    <p>{t("Технологии, производство и люди. События, которыми живёт «Сеспель».")}</p>
                </header>

                {/* Блок категорий */}
                {categories.length > 0 && (
                    <div className={`category-filter ${theme}`}>
                        <div className="category-filter__title">{t("Категории")}</div>
                        <div className="category-filter__list">
                            {categories.map(({ category, label, count }) => (
                                <button type="button" aria-pressed={selectedCategory === category}
                                    key={category}
                                    className={`category-filter__item ${selectedCategory === category ? 'active' : ''}`}
                                    onClick={() => handleCategoryClick(category)}
                                >
                                    <span className="category-filter__label">{t(label)}</span>
                                    <span className="category-filter__count">{count}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Блок годов */}
                {availableYears.length > 0 && (
                    <div className={`year-filter ${theme}`}>
                        <div className="year-filter__title">{t("Год")}</div>
                        <div className="year-filter__list">
                            {availableYears.map(year => (
                                <button type="button" aria-pressed={selectedYear === year}
                                    key={year}
                                    className={`year-filter__item ${selectedYear === year ? 'active' : ''}`}
                                    onClick={() => handleYearClick(year)}
                                >
                                    {year}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Кнопка сброса фильтров */}
                {hasActiveFilters && (
                    <div className={`category-clear ${theme}`}>
                        <button onClick={clearFilters}>{t("Показать все")}</button>
                    </div>
                )}

                <div className="news-results-toolbar" ref={resultsRef} tabIndex={-1}>
                    <p className="news-results-count" role="status">
                        {title} · {t("Публикаций")}: {filteredNews.length}
                        {filteredNews.length > 0 && <span> · {pageStart + 1}–{pageStart + visibleNews.length}</span>}
                    </p>
                    <div className="news-view-switch" role="group" aria-label={t("Вид новостей")}>
                        <button type="button" aria-pressed={view === 'cards'} onClick={() => setView('cards')}>{t("Карточки")}</button>
                        <button type="button" aria-pressed={view === 'list'} onClick={() => setView('list')}>{t("Список")}</button>
                    </div>
                </div>
                {filteredNews.length === 0 ? (
                    <p className={`noNews ${theme}`}>{t("Новостей по выбранным фильтрам не найдено.")}</p>
                ) : (
                    <div className={`news-cards-grid ${view === 'list' ? 'news-cards-list' : ''}`}>{visibleNews.map(news => (
                        <NewsPreviewCard key={news.url} news={localizeNews(news, english)} />
                    ))}</div>
                )}
                {totalPages > 1 && <nav className="news-pagination" aria-label={t("Страницы новостей")}>
                    <button type="button" aria-label={t("Предыдущая страница")} disabled={currentPage === 1} onClick={() => changePage(currentPage - 1)}>←</button>
                    {pageNumbers.map((number, index) => <React.Fragment key={number}>
                        {index > 0 && number - pageNumbers[index - 1] > 1 && <span aria-hidden="true">…</span>}
                        <button type="button" aria-label={`${t("Страница")} ${number}`} aria-current={currentPage === number ? 'page' : undefined} onClick={() => changePage(number)}>{number}</button>
                    </React.Fragment>)}
                    <button type="button" aria-label={t("Следующая страница")} disabled={currentPage === totalPages} onClick={() => changePage(currentPage + 1)}>→</button>
                </nav>}
            </div>
        </>
    );
}

export function News() {
    const {english, t} = useTranslation();
    const location = useLocation();
    const { newsData, refreshNews } = useNews();
    const { theme } = useTheme();

    // Получаем ключ текущего маршрута
    const pathNames = location.pathname.replace(/^\/en(?=\/|$)/, "").split("/").filter((x) => x);
    const currentKey = Number(pathNames[1]);

    // Защита от пустого newsData
    const currentNews = newsData.length > 0
        ? newsData.find(news => news.url === `/news/${currentKey}`)
        : null;

    const sourceContent = currentNews ? NewsContentJson[currentNews.title] : null;
    const translation = english ? getNewsTranslation(currentNews) : null;
    const currentContent = translation ? {...sourceContent, content: translation.content} : sourceContent;
    const displayNews = localizeNews(currentNews, english);

    // Используем useRef для отслеживания, была ли уже обработана эта новость
    const processedNewsRef = useRef(new Set());

    useEffect(() => {
        if (!currentNews) return;

        const newsId = currentNews.title;
        if (processedNewsRef.current.has(newsId)) {
            return;
        }

        processedNewsRef.current.add(newsId);

        async function addViewAndRefresh() {
            try {
                const UUID = getUUID();
                await fetchRequestNewsView(currentNews.title, UUID);
                await refreshNews();
            } catch (error) {
                console.log("error", error);
                try {
                    await refreshNews();
                } catch (refreshError) {
                    console.log("refresh error", refreshError);
                }
            }
        }

        addViewAndRefresh();
        window.scrollTo(0, 0);
    }, [currentNews, refreshNews, location.pathname]);

    // ---- Определяем индекс текущей новости в массиве (от новых к старым) ----
    const currentIndex = currentNews ? newsData.findIndex(n => n.title === currentNews.title) : -1;
    const olderNews = currentIndex >= 0 && currentIndex < newsData.length - 1 ? newsData[currentIndex + 1] : null;
    // Более новая новость (индекс - 1) – будет "Следующая"
    const newerNews = currentIndex > 0 ? newsData[currentIndex - 1] : null;

    // Если новость не найдена
    if (!currentNews) {
        return (
            <>
                <SeoHelmet title={t("Новость не найдена")} description={t("Запрошенная новость не существует")} />
                <Navigation />
                <div className="complexBack"> </div>
                <div className={`news-card-description ${theme}`}>
                    <div>{t("Новость не найдена")}</div>
                </div>
            </>
        );
    }

    return (
        <>
            <SeoHelmet title={displayNews.title} description={displayNews.previewText} canonicalUrl={newsPath(currentNews.url, english)} />
            <div className={`complexBack ${theme}`}> </div>
            <article className={`news-card-description news-article news-redesign ${theme}`}>
                <Navigation />
                <Link to={newsPath("/news", english)} className="news-back-link">{t("← Все новости")}</Link>
                <div className="news-meta-container">
                    <i className={`news-date ${theme}`}>{currentNews.date}</i>
                    <div className="news-views">
                        <EyeOutlined className="eye-icon" />
                        <span className={`views-count ${theme}`}>{currentNews.views || 0}</span>
                    </div>
                </div>
                <h1>{displayNews.title}</h1>
                {displayNews.untranslated && <p role="note">This article is currently available in Russian.</p>}
                {!currentContent?.images?.length ? null : (
                    <NewsPhoto images={currentContent.images.map(e => getImage(e))} />
                )}
                <div className="news-content" lang={displayNews.untranslated ? "ru" : undefined}>
                    <p lang={displayNews.untranslated ? "ru" : undefined}>{displayNews.previewText}</p>
                    {currentContent && currentContent.content
                        ? currentContent.content.map((e, i) => (
                            <React.Fragment key={`content${i}`}>
                                {htmlConverter(e)}
                            </React.Fragment>
                        ))
                        : null}
                </div>

                {/* ---------- Навигация (Предыдущая / Следующая) ---------- */}
                {(olderNews || newerNews) && (
                    <div className={`news-navigation ${theme}`}>
                        <div className="news-navigation__inner">
                            {olderNews && (
                                <Link to={newsPath(olderNews.url, english)} className="news-navigation__link">
                                    <img src={getImage(olderNews.preview)} alt={localizeNewsTitle(olderNews, english)} className="news-navigation__image" />
                                    <div className="news-navigation__text">
                                        <span className="news-navigation__label">{t("← Предыдущая")}</span>
                                        <span className="news-navigation__title">{localizeNewsTitle(olderNews, english)}</span>
                                    </div>
                                </Link>
                            )}
                            {newerNews && (
                                <Link to={newsPath(newerNews.url, english)} className="news-navigation__link">
                                    <div className="news-navigation__text">
                                        <span className="news-navigation__label">{t("Следующая →")}</span>
                                        <span className="news-navigation__title">{localizeNewsTitle(newerNews, english)}</span>
                                    </div>
                                    <img src={getImage(newerNews.preview)} alt={localizeNewsTitle(newerNews, english)} className="news-navigation__image" />
                                </Link>
                            )}
                        </div>
                    </div>
                )}
                {/* ---------- Конец блока ---------- */}
            </article>
        </>
    );
}

export function htmlConverter(object) {

    const key = Object.keys(object)[0]
    const content = object[key]

    switch (key) {
        case "p":
            return <p>{content}</p>
        case "a":
            return <p>{content.text}{<Link to={content.to}> {content.linkText}</Link>}</p>
        case "h1":
            return <h1>{content}</h1>
        case "h2":
            return <h2>{content}</h2>
        case "h3":
            return <h3>{content}</h3>
        case "strong":
            return <strong>{content}</strong>
        case "img":
            return <img src={getImage(content)} alt="no-img"/>
        case "ul":
            return <ul>
                {content.map(li=>
                    <li key={li}>{li}</li>
                )}
            </ul>
        case "productParams":
            return <ProductListParams params={content}/>
        case "singleList":
            return <SingleList list={content}/>
        case "doubleList":
            return <DoubleList list={content}/>
        case "numberList":
            return <NoStrongList list={content} numbered={true}/>
        case "table":
            return <TableConstructor data={content}/>
        case "silosModels":
            return <SilosModelsConstructor data={content}/>
        case "galleryList":
            return <GalleryDescription data={content}/>
        case "galleryLinks":
            return <GalleryDescriptionLinks data={content}/>
        case "doc":
            return <DocumentationTable documents={content}/>

        default:
            return <>{content}</>
    }
}
